import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../services/api';
import { useAuthStore } from '../store/authStore';
import { Play, Square, X, AlertTriangle } from 'lucide-react';
import type { Ticket } from '../types';
import TimerSummaryModal from './TimerSummaryModal';

function formatElapsed(startedAt: string): string {
  const diff = Math.max(0, Date.now() - new Date(startedAt).getTime());
  const h = Math.floor(diff / 3600000);
  const m = Math.floor((diff % 3600000) / 60000);
  const s = Math.floor((diff % 60000) / 1000);
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
}

function StartTimerModal({ onClose, onStart }: {
  onClose: () => void;
  onStart: (ticketId: string) => void;
}) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [selectedOrgId, setSelectedOrgId] = useState<string>('');
  const [selectedProjectId, setSelectedProjectId] = useState<string>('');

  const user = useAuthStore(s => s.user);
  const isAdmin = user?.role === 'admin';

  const { data: orgsData } = useQuery({
    queryKey: ['organizations'],
    queryFn: () => api.getOrganizations(),
    enabled: isAdmin,
    staleTime: 300_000,
  });

  const { data: projectsData } = useQuery({
    queryKey: ['projects', selectedOrgId],
    queryFn: () => api.getProjects({
      organization_id: selectedOrgId || undefined,
      status: 'active',
    }),
    staleTime: 300_000,
  });

  const { data: ticketsData, isLoading } = useQuery({
    queryKey: ['tickets', 'timer-select', selectedOrgId, selectedProjectId, search],
    queryFn: () => api.getTickets({
      limit: 100,
      status: 'open',
      search: search || undefined,
      organization_id: isAdmin && selectedOrgId ? selectedOrgId : undefined,
      project_id: selectedProjectId || undefined,
    }),
  });

  const tickets: Ticket[] = ticketsData?.data ?? [];

  return (
    <div className="modal-backdrop z-[60]"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}>
      <div className="modal-content max-w-md w-full p-6" dir="rtl">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-bold text-surface-900">{t('timer.selectTicket')}</h2>
          <button onClick={onClose} aria-label={t('aria.close')} className="text-surface-400 hover:text-surface-600"><X size={20} /></button>
        </div>

        {isAdmin && (
          <div className="mb-2">
            <label className="text-xs text-surface-500 mb-1 block">{t('timer.filterByOrg')}</label>
            <select
              value={selectedOrgId}
              onChange={(e) => { setSelectedOrgId(e.target.value); setSelectedProjectId(''); }}
              className="input text-sm w-full"
            >
              <option value="">{t('timer.allOrganizations')}</option>
              {(orgsData?.data ?? []).map(org => (
                <option key={org.id} value={org.id}>{org.name}</option>
              ))}
            </select>
          </div>
        )}

        <div className="mb-2">
          <label className="text-xs text-surface-500 mb-1 block">{t('timer.filterByProject')}</label>
          <select
            value={selectedProjectId}
            onChange={(e) => setSelectedProjectId(e.target.value)}
            className="input text-sm w-full"
          >
            <option value="">{t('timer.allProjects')}</option>
            {(projectsData?.data ?? []).map(proj => (
              <option key={proj.id} value={proj.id}>{proj.name}</option>
            ))}
          </select>
        </div>

        <input
          type="text"
          placeholder={t('timer.searchTicket')}
          className="input mb-3 text-sm"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoFocus
        />
        <div className="max-h-60 overflow-y-auto space-y-1">
          {isLoading ? (
            <p className="text-sm text-surface-400 text-center py-4">...</p>
          ) : tickets.length === 0 ? (
            <p className="text-sm text-surface-400 text-center py-4">{t('timer.noTicketsFound')}</p>
          ) : (
            tickets.map((ticket) => (
              <button
                key={ticket.id}
                onClick={() => onStart(ticket.id)}
                className="w-full text-right px-3 py-2 rounded-lg hover:bg-primary-50 transition-colors text-sm text-surface-700"
              >
                <div className="flex items-center gap-2">
                  <Play size={14} className="text-primary-600 flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <span className="truncate block font-medium">{ticket.title}</span>
                    <span className="text-xs text-surface-400 truncate block">
                      {ticket.organization_name}
                      {ticket.project_name && ` · ${ticket.project_name}`}
                    </span>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </div>
    </div>
  );
}

function TimerRecoveryModal({ timer, onSave, onDismiss }: {
  timer: { timer_started_at: string; timer_ticket_id: string; ticket_title: string };
  onSave: () => void;
  onDismiss: () => void;
}) {
  const { t } = useTranslation();
  const started = new Date(timer.timer_started_at);
  const formattedTime = started.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' });
  const formattedDate = started.toLocaleDateString('he-IL');

  return (
    <div className="modal-backdrop z-[60]">
      <div className="modal-content max-w-md w-full p-6" dir="rtl">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-warning-100 rounded-full">
            <AlertTriangle size={24} className="text-warning-600" />
          </div>
          <div>
            <h2 className="text-lg font-bold text-surface-900">{t('timer.suspended')}</h2>
            <p className="text-sm text-surface-500">
              {t('timer.suspendedMessage')}{formattedTime} ({formattedDate})
            </p>
          </div>
        </div>
        <p className="text-sm text-surface-700 mb-4">
          {t('timer.ticket')}<span className="font-medium">{timer.ticket_title}</span>
        </p>
        <div className="flex gap-3">
          <button onClick={onSave} className="btn btn-primary flex-1">{t('timer.saveWorkLog')}</button>
          <button onClick={onDismiss} className="btn btn-secondary flex-1">{t('timer.dismiss')}</button>
        </div>
      </div>
    </div>
  );
}

export default function GlobalTimer() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const [showStartModal, setShowStartModal] = useState(false);
  const [showSummary, setShowSummary] = useState(false);
  const [showRecovery, setShowRecovery] = useState(false);
  const [elapsed, setElapsed] = useState('00:00:00');
  const [stopResult, setStopResult] = useState<any>(null);

  const isAgentOrAdmin = user && (user.role === 'admin' || user.role === 'agent');

  const { data: timerData, isLoading } = useQuery({
    queryKey: ['timer-status'],
    queryFn: () => api.getTimerStatus(),
    enabled: !!isAgentOrAdmin,
    refetchInterval: 30000,
  });

  const timer = timerData?.data ?? null;
  const isRunning = timer && timer.timer_status === 'active';
  const isSuspended = timer && timer.timer_status === 'suspended';

  useEffect(() => {
    if (isSuspended && !showRecovery && !showSummary) {
      setShowRecovery(true);
    }
  }, [isSuspended, showRecovery, showSummary]);

  useEffect(() => {
    if (!isRunning || !timer) return;
    const tick = () => setElapsed(formatElapsed(timer.timer_started_at));
    tick();
    const interval = setInterval(tick, 1000);
    return () => clearInterval(interval);
  }, [isRunning, timer?.timer_started_at]);

  const startMutation = useMutation({
    mutationFn: (ticketId: string) => api.startTimer(ticketId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['timer-status'] });
      setShowStartModal(false);
    },
  });

  const stopMutation = useMutation({
    mutationFn: () => api.stopTimer(),
    onSuccess: (data) => {
      setStopResult(data.data);
      setShowSummary(true);
      queryClient.invalidateQueries({ queryKey: ['timer-status'] });
    },
  });

  const handleRecoverySave = useCallback(() => {
    setShowRecovery(false);
    stopMutation.mutate();
  }, [stopMutation]);

  const handleRecoveryDismiss = useCallback(async () => {
    setShowRecovery(false);
    await api.stopTimer();
    queryClient.invalidateQueries({ queryKey: ['timer-status'] });
  }, [queryClient]);

  if (!isAgentOrAdmin || isLoading) return null;

  return (
    <>
      {/* Floating chip */}
      <div className="fixed bottom-6 left-6 z-50" dir="rtl">
        {(startMutation.isError || stopMutation.isError) && (
          <div className="mb-2 bg-danger-50 border border-danger-100 text-danger-700 px-3 py-2 rounded-lg text-xs shadow-elevated">
            {startMutation.isError ? t('timer.startError') : t('timer.stopError')}
          </div>
        )}
        {isRunning && timer ? (
          <div className="flex items-center gap-2 bg-surface-0 border-2 border-primary-500 rounded-full shadow-elevated px-4 py-2">
            <div className="w-2 h-2 rounded-full bg-danger-500 animate-pulse" />
            <span className="text-sm font-mono font-bold text-surface-800">{elapsed}</span>
            <span className="text-xs text-surface-500 max-w-[120px] truncate">{timer.ticket_title}</span>
            <button
              onClick={() => stopMutation.mutate()}
              disabled={stopMutation.isPending}
              className="p-1.5 bg-danger-100 hover:bg-red-200 rounded-full transition-colors"
              title={t('timer.stop')}
            >
              <Square size={14} className="text-danger-600" />
            </button>
          </div>
        ) : (
          <button
            onClick={() => setShowStartModal(true)}
            className="flex items-center gap-2 bg-primary-600 hover:bg-primary-700 text-white rounded-full shadow-elevated px-4 py-2 transition-colors"
          >
            <Play size={16} />
            <span className="text-sm font-medium">{t('timer.startWork')}</span>
          </button>
        )}
      </div>

      {showStartModal && (
        <StartTimerModal
          onClose={() => setShowStartModal(false)}
          onStart={(ticketId) => startMutation.mutate(ticketId)}
        />
      )}

      {showSummary && stopResult && (
        <TimerSummaryModal
          ticketId={stopResult.ticket_id}
          ticketTitle={stopResult.ticket_title}
          startTime={stopResult.start_time}
          endTime={stopResult.end_time}
          durationMinutes={stopResult.duration_minutes}
          onClose={() => { setShowSummary(false); setStopResult(null); }}
          onSuccess={() => {
            setShowSummary(false);
            setStopResult(null);
            queryClient.invalidateQueries({ queryKey: ['work-logs'] });
          }}
        />
      )}

      {showRecovery && timer && (
        <TimerRecoveryModal
          timer={timer as any}
          onSave={handleRecoverySave}
          onDismiss={handleRecoveryDismiss}
        />
      )}
    </>
  );
}
